import * as React from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal, type LucideIcon } from "lucide-react";
import { cn } from "../../lib/cn.js";
import { IconButton } from "./IconButton.js";

const GAP = 4;
const VIEWPORT_MARGIN = 8;

interface Position {
  top: number;
  left: number;
  maxHeight: number;
}

function computePosition(
  triggerRect: DOMRect,
  menuHeight: number,
): Position {
  const spaceBelow = window.innerHeight - triggerRect.bottom - GAP - VIEWPORT_MARGIN;
  const spaceAbove = triggerRect.top - GAP - VIEWPORT_MARGIN;

  let top: number;
  let maxHeight: number;

  if (spaceBelow >= menuHeight || spaceBelow >= spaceAbove) {
    top = triggerRect.bottom + GAP;
    maxHeight = Math.max(80, window.innerHeight - top - VIEWPORT_MARGIN);
  } else {
    top = Math.max(VIEWPORT_MARGIN, triggerRect.top - GAP - menuHeight);
    maxHeight = Math.max(80, triggerRect.top - GAP - VIEWPORT_MARGIN);
  }

  const left = Math.max(VIEWPORT_MARGIN, triggerRect.right);

  return { top, left, maxHeight };
}

// ============================================================================
// Portal overlay
// ============================================================================

interface OverlayProps {
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  children: React.ReactNode;
}

function ActionOverflowMenuPortal({ onClose, triggerRef, children }: OverlayProps) {
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<Position | null>(null);
  const [measured, setMeasured] = React.useState(false);

  const reposition = React.useCallback(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuHeight = menu ? menu.scrollHeight : 200;
    setPos(computePosition(rect, menuHeight));
  }, [triggerRef]);

  React.useLayoutEffect(() => {
    reposition();
    requestAnimationFrame(() => {
      reposition();
      setMeasured(true);
    });
  }, [reposition]);

  React.useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [onClose, reposition]);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[100]" onClick={onClose} aria-hidden />
      <div
        ref={menuRef}
        role="menu"
        className="fixed z-[110] min-w-[180px] overflow-hidden overflow-y-auto rounded-lg border border-[var(--lm-border-subtle)] bg-white shadow-lg dark:border-white/10 dark:bg-slate-800"
        style={{
          top: pos?.top ?? -9999,
          left: pos?.left ?? -9999,
          transform: "translateX(-100%)",
          maxHeight: pos?.maxHeight,
          visibility: measured ? "visible" : "hidden",
        }}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}

// ============================================================================
// Public API
// ============================================================================

export interface ActionOverflowMenuProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children" | "onClick"> {
  children: React.ReactNode;
  /** Hide the trigger when there are no visible menu items */
  hideWhenEmpty?: boolean;
  /** Trigger button size. Defaults to the standard small control. */
  size?: "xs" | "sm";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export function ActionOverflowMenu({
  children,
  className,
  hideWhenEmpty = true,
  size = "sm",
  onClick,
  ...props
}: ActionOverflowMenuProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const items = React.Children.toArray(children).filter(Boolean);

  if (hideWhenEmpty && items.length === 0) {
    return null;
  }

  return (
    <div className={cn("relative shrink-0", className)} {...props}>
      <IconButton
        ref={triggerRef}
        type="button"
        size={size}
        title="Actions"
        aria-label="Actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          onClick?.(e);
          setIsOpen((o) => !o);
        }}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </IconButton>

      {isOpen ? (
        <ActionOverflowMenuPortal
          onClose={() => setIsOpen(false)}
          triggerRef={triggerRef}
        >
          {React.Children.map(items, (child) => {
            if (!React.isValidElement<ActionOverflowMenuItemProps>(child)) {
              return child;
            }
            return React.cloneElement(child, {
              onItemClick: () => setIsOpen(false),
            });
          })}
        </ActionOverflowMenuPortal>
      ) : null}
    </div>
  );
}

// ============================================================================
// Menu item
// ============================================================================

export interface ActionOverflowMenuItemProps {
  icon: LucideIcon;
  label: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: "default" | "destructive";
  disabled?: boolean;
  onItemClick?: () => void;
}

export function ActionOverflowMenuItem({
  icon: Icon,
  label,
  onClick,
  variant = "default",
  disabled = false,
  onItemClick,
}: ActionOverflowMenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={(event) => {
        if (disabled) return;
        onClick?.(event);
        if (!event.defaultPrevented) {
          onItemClick?.();
        }
      }}
      className={cn(
        "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors disabled:pointer-events-none disabled:opacity-50",
        variant === "destructive"
          ? "text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-500/10"
          : "text-[#576274] hover:bg-[var(--lm-hover)] hover:text-[#101828] dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white",
      )}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <span>{label}</span>
    </button>
  );
}
